import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import Checkbox from '@repo/ui/checkbox'
import DataTable, { Column } from '@repo/ui/data-table'
import { DeleteIcon, PlusIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'

import { getJourney } from '../../../../api/products/get-journey'
import useTranslate from '../../../../lib/hooks/use-translate'
import {
  FriendPlaceEnum,
  friendPlaceEnumMapping
} from '../../../../lib/types/enums/friend/friend-place.enum'
import { FamilyArrangementEnum } from '../../../../lib/types/enums/ol/family-arrangement.enum'
import { AccomodationCategoryEnum } from '../../../../lib/types/enums/product/accomodation-category.enum'
import { EnrollmentPropositionInput } from '../../../../lib/types/models/enrollment-proposition/enrollment-proposition-input'
import { FriendInput } from '../../../../lib/types/models/friend/friend-input'
import { IdInput } from '../../../../lib/types/utils'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import { FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledRadioInput from '../../../elements/inputs/controlled-radio-input'

type Props = {
  journeyId: number
  onSubmit: () => void
  onCancel: () => void
}

type FamilyArrangementState = 'BOTH' | 'ONE' | 'TWO' | 'NONE'

const EnrollmentHospitalityConditions = ({ journeyId, onCancel, onSubmit }: Props) => {
  const form = useFormContext<EnrollmentPropositionInput>()
  const { t } = useTranslate()

  const { data: journey, isPending } = useQuery({
    queryKey: ['journey', journeyId],
    queryFn: () => getJourney(journeyId)
  })

  const familyArrangementState: FamilyArrangementState = useMemo(() => {
    if (journey?.fa1eIncluded && journey.fa2eIncluded) return 'BOTH'
    else if (journey?.fa1eIncluded) return 'ONE'
    else if (journey?.fa2eIncluded) return 'TWO'
    return 'NONE'
  }, [journey])

  const { declaredFriends, options } = form.watch()

  const columns = useMemo<Column<IdInput<FriendInput>>[]>(
    () => [
      {
        accessorKey: 'lastName',
        header: 'NOM',
        cell: ({ row }) => (
          <FormField
            control={form.control}
            name={`declaredFriends.${row.index}.lastName`}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput {...field} type={'text'} label={'NOM'} />
                </FormControl>
              </FormItem>
            )}
          />
        )
      },
      {
        accessorKey: 'firstName',
        header: 'Prénom',
        cell: ({ row }) => (
          <FormField
            control={form.control}
            name={`declaredFriends.${row.index}.firstName`}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput {...field} type={'text'} label={'Prénom'} />
                </FormControl>
              </FormItem>
            )}
          />
        )
      },
      {
        accessorKey: 'type',
        header: 'Demande',
        cell: ({ row }) => {
          if (
            journey?.date.product.accomodationCategory ===
            AccomodationCategoryEnum.COLLEGE_CAMPUS_RESIDENCE
          ) {
            return (
              <Typography variant="label-medium">
                {t(friendPlaceEnumMapping[FriendPlaceEnum.SAME_CENTER].label)}
              </Typography>
            )
          }
          return (
            <div className="flex gap-6">
              {Object.values(FriendPlaceEnum).map((key) => (
                <FormField
                  key={key}
                  control={form.control}
                  name={`declaredFriends.${row.index}.type`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <ControlledRadioInput
                          {...field}
                          label={t(friendPlaceEnumMapping[key].label)}
                          checked={field.value === key}
                          onCheck={() => field.onChange(key)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              ))}
            </div>
          )
        }
      },
      {
        accessorKey: 'id',
        header: '',
        cell: ({ row }) => (
          <ActionButton
            variant={'danger'}
            icon={DeleteIcon}
            onClick={(e) => {
              e.preventDefault()
              form.setValue(
                'declaredFriends',
                form.getValues().declaredFriends?.filter((_, index) => index !== row.index)
              )
            }}
          />
        )
      }
    ],
    [declaredFriends]
  )

  const familyArrangement = form.watch('familyArrangement')
  useEffect(() => {
    if (familyArrangementState === 'ONE')
      form.setValue('familyArrangement', FamilyArrangementEnum.FA1E)
    else if (familyArrangementState === 'TWO')
      form.setValue('familyArrangement', FamilyArrangementEnum.FA2E)
    // a stay offering no family arrangement rejects one (e.g. copied from a duplicated request)
    else if (journey && familyArrangementState === 'NONE')
      form.setValue('familyArrangement', undefined)
  }, [journey, familyArrangementState])

  if (!journey || isPending) return <Loader center />

  return (
    <div>
      <div className="h-4" />
      <Typography variant="tag">Option(s) facultative(s)</Typography>
      <div className="h-3" />
      <div className="flex flex-col gap-2">
        {journey.secondaryOptions.length > 0 ? (
          journey.secondaryOptions.map((secondaryOption) => (
            <div key={secondaryOption.option.id} className="flex items-center gap-2">
              <Checkbox
                label={getTranslatedName(secondaryOption.option)}
                checked={options?.map((option) => option.id).includes(secondaryOption.option.id)}
                onChange={() => {
                  if (options?.map((option) => option.id)?.includes(secondaryOption.option.id)) {
                    form.setValue(
                      'options',
                      options?.filter((option) => option.id !== secondaryOption.option.id)
                    )
                  } else form.setValue('options', [...(options ?? []), secondaryOption.option])
                }}
              />
              <Badge variant="primary" text={`${secondaryOption.price} €`} className="h-5" />
            </div>
          ))
        ) : (
          <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
            Aucune option n'est disponible pour ce séjour
          </Typography>
        )}
      </div>
      <div className="h-5" />
      <Typography variant="tag">
        Modalités d'accueil {familyArrangementState !== 'NONE' && '*'}
      </Typography>
      <div className="h-3" />
      {familyArrangementState === 'BOTH' && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <FormField
              control={form.control}
              name={'familyArrangement'}
              render={({ field }) => (
                <FormControl>
                  <ControlledRadioInput
                    {...field}
                    label={'Un français par famille'}
                    checked={familyArrangement === FamilyArrangementEnum.FA1E}
                    onCheck={() => form.setValue('familyArrangement', FamilyArrangementEnum.FA1E)}
                  />
                </FormControl>
              )}
            />
            <Badge variant="primary" text={`${journey.fa1ePrice} €`} className="h-5" />
          </div>
          <div className="flex items-center gap-2">
            <FormField
              control={form.control}
              name={'familyArrangement'}
              render={({ field }) => (
                <FormControl>
                  <ControlledRadioInput
                    {...field}
                    label={'Deux français par famille'}
                    checked={familyArrangement === FamilyArrangementEnum.FA2E}
                    onCheck={() => form.setValue('familyArrangement', FamilyArrangementEnum.FA2E)}
                  />
                </FormControl>
              )}
            />
            <Badge variant="primary" text={`${journey.fa2ePrice} €`} className="h-5" />
          </div>
        </div>
      )}
      {familyArrangementState === 'ONE' && (
        <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
          {`Un français par famille - ${journey.fa1ePrice} €`}
        </Typography>
      )}
      {familyArrangementState === 'TWO' && (
        <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
          {`Deux français par famille - ${journey.fa2ePrice} €`}
        </Typography>
      )}
      {familyArrangementState === 'NONE' && (
        <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
          Aucune modalité d'accueil n'est disponible pour ce séjour
        </Typography>
      )}
      <div className="h-5" />
      <Typography variant="tag">Ajouter un ami du participant</Typography>
      <div className="h-3" />
      <DataTable<IdInput<FriendInput>>
        columns={columns}
        data={
          declaredFriends?.map((friend, index) => ({
            ...friend,
            id: index
          })) ?? []
        }
        rowCount={declaredFriends?.length ?? 0}
        paginated={false}
        disableHover
        displayEmptyState={false}
        footer={
          <Button
            variant={'secondary'}
            icon={PlusIcon}
            label={'Ajouter un ami'}
            type={'button'}
            className={'border-none'}
            onClick={() => {
              const updatedList = [
                ...(form.watch().declaredFriends ?? []),
                { firstName: '', lastName: '', type: FriendPlaceEnum.SAME_FAMILY }
              ]
              form.setValue('declaredFriends', updatedList)
            }}
          />
        }
      />
      <div className="h-6" />
      <div className="flex w-full justify-end mb-4 gap-3">
        <Button type="button" variant="ghost" onClick={onCancel} label="Retour" />
        <Button
          type="button"
          variant="primary"
          disabled={
            ((journey.fa1eIncluded || journey.fa2eIncluded) && !form.watch('familyArrangement')) ||
            !!form
              .watch('declaredFriends')
              ?.find((friend) => !friend.firstName || !friend.lastName)
          }
          onClick={onSubmit}
          label="Suivant"
        />
      </div>
    </div>
  )
}

export default EnrollmentHospitalityConditions
